/**
 * The Apollo form treatment, at theme level so every `ui/forms/*`, `ui/text-field` and `ui/select`
 * inherits it rather than restating borders and focus rings per field. This is the port of Chakra's
 * `sharedFormStyles`. Colours are read off the palette; the focus rings and the 6px corner stay on
 * `design-tokens` because the theme has no slot for either — `aqua-100` and `critical-100` have no
 * palette step, and `shape.borderRadius` is 8.
 */

import { type Theme } from "@mui/material/styles";

import { type ComponentOverrides } from "theme/theme.types";

import {
  CONTROL_PADDING,
  outlinedInputBase,
  outlinedInputSizeStyle,
} from "theme/components/field-geometry";
import { sizeVariants } from "theme/theme.helpers";
import { TYPE_SCALE } from "theme/typography";

import { borderRadius, shadows } from "design-tokens";

/**
 * The control box — background, corner, border and focus ring — for any outlined control. A parameter
 * rather than one selector because `ui/date-picker`'s field is not an `OutlinedInput`: MUI X draws it
 * with its own `PickersOutlinedInput`, whose outline carries a different class.
 */
export const outlinedControlRoot = (theme: Theme, notchedOutline: string) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  // Figma draws inputs at 6px, against the global `shape.borderRadius` of 8. The notched
  // outline inherits it, so the `fieldset` needs no rule of its own.
  borderRadius: borderRadius.md,
  [`& ${notchedOutline}`]: {
    borderColor: theme.palette.border.secondary,
  },
  [`&:hover ${notchedOutline}`]: {
    borderColor: theme.palette.primary.main,
  },
  // A 1px border plus a shadow ring; MUI would thicken the border to 2px and draw no ring.
  "&.Mui-focused": {
    boxShadow: shadows.input,
    [`& ${notchedOutline}`]: {
      borderWidth: 1,
      borderColor: theme.palette.primary.main,
    },
  },
  "&.Mui-error": {
    boxShadow: "none",
    [`& ${notchedOutline}`]: {
      borderColor: theme.palette.error.main,
    },
    [`&:hover ${notchedOutline}`]: {
      borderColor: theme.palette.error.main,
    },
    "&.Mui-focused": {
      boxShadow: shadows["input-invalid"],
      [`& ${notchedOutline}`]: {
        borderColor: theme.palette.error.main,
      },
    },
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.grey[100],
    cursor: "not-allowed",
    [`& ${notchedOutline}`]: {
      borderColor: theme.palette.border.secondary,
    },
  },
});

export const MuiOutlinedInput: ComponentOverrides["MuiOutlinedInput"] = {
  styleOverrides: {
    root: ({ theme }) => ({
      ...outlinedControlRoot(theme, ".MuiOutlinedInput-notchedOutline"),
      ...outlinedInputBase(theme),
      variants: sizeVariants(CONTROL_PADDING, (size) => outlinedInputSizeStyle(theme, size)),
    }),
    input: ({ theme }) => ({
      "&::placeholder": {
        color: theme.palette.text.disabled,
        opacity: 1,
      },
      "&.Mui-disabled": {
        cursor: "not-allowed",
        // A disabled value keeps its body colour. The property is overridden rather than dropped:
        // MUI's own `InputBase` paints `text.disabled` through `-webkit-text-fill-color`, which
        // beats `color`, so removing this line hands the grey back.
        WebkitTextFillColor: theme.palette.text.primary,
      },
    }),
  },
};

/** Always body-coloured and bold — the label does not tint on focus or error. */
export const MuiFormLabel: ComponentOverrides["MuiFormLabel"] = {
  styleOverrides: {
    root: ({ theme }) => ({
      color: theme.palette.text.primary,
      fontWeight: 700,
      "&.Mui-focused": { color: theme.palette.text.primary },
      "&.Mui-error": { color: theme.palette.text.primary },
      "&.Mui-disabled": { color: theme.palette.text.primary, opacity: 1 },
    }),
    // MUI tints the asterisk only on error, so with `root` pinning the label colour it would
    // otherwise read as body text.
    asterisk: ({ theme }) => ({
      color: theme.palette.error.main,
    }),
  },
};

/**
 * Only the *resting* colour is pinned — `ui/forms/field-message` renders `<FormHelperText error>` for the
 * error tone and must stay `palette.error.main`. `marginLeft: 0` undoes MUI's `contained` 14px
 * indent, which clears a notched-outline label gap Apollo does not have.
 *
 * `marginTop: 0` overrides MUI's own 3px (4px at `size="small"`) because the gap below a control is
 * owned by the field container, not the message: `InputControl` spaces its children with `gap`, and
 * so does every hand-rolled field wrapper around `ui/select` and the form builder. Leaving a margin
 * here would add to that gap rather than replace it.
 */
export const MuiFormHelperText: ComponentOverrides["MuiFormHelperText"] = {
  styleOverrides: {
    root: ({ theme }) => ({
      color: theme.palette.text.primary,
      ...TYPE_SCALE.sm,
      marginTop: 0,
      marginLeft: 0,
      "&.Mui-disabled": {
        color: theme.palette.text.primary,
      },
    }),
  },
};
